import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2, Pencil, X, Check, Copy, Plus } from "lucide-react";
import { downloadCSV } from "@/lib/csv";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const KINDS = ["grammar", "vocab", "reading"];
const AUDIENCES = [
  { value: "primary", label: "Primary (ages 6–11)" },
  { value: "teen", label: "Teen (ages 12–17)" },
  { value: "general", label: "General (adult / mixed)" },
];

const EMPTY_Q = { audience: "general", level: "B1", kind: "grammar", passage: "", prompt: "", options: ["", "", "", ""], answer_index: 0 };

function QuestionForm({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  function setOption(i, v) {
    const next = [...value.options]; next[i] = v;
    onChange({ ...value, options: next });
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Audience</Label>
        <Select value={value.audience || "general"} onValueChange={(v)=>set("audience", v)}>
          <SelectTrigger data-testid="cefr-q-audience" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{AUDIENCES.map(a=><SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Level</Label>
        <Select value={value.level} onValueChange={(v)=>set("level", v)}>
          <SelectTrigger data-testid="cefr-q-level" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Kind</Label>
        <Select value={value.kind} onValueChange={(v)=>set("kind", v)}>
          <SelectTrigger data-testid="cefr-q-kind" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{KINDS.map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Correct option</Label>
        <Select value={String(value.answer_index)} onValueChange={(v)=>set("answer_index", parseInt(v, 10))}>
          <SelectTrigger data-testid="cefr-q-answer" className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>
            {value.options.map((_, i)=><SelectItem key={i} value={String(i)}>Option {String.fromCharCode(65+i)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-12">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Passage (optional, for reading-comp items)</Label>
        <Textarea rows={2} value={value.passage || ""} onChange={(e)=>set("passage", e.target.value)} data-testid="cefr-q-passage" className="mt-2" />
      </div>
      <div className="md:col-span-12">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Prompt / question</Label>
        <Textarea rows={2} value={value.prompt} onChange={(e)=>set("prompt", e.target.value)} data-testid="cefr-q-prompt" className="mt-2" />
      </div>
      {value.options.map((opt, i) => (
        <div key={i} className="md:col-span-6">
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Option {String.fromCharCode(65+i)} {i === value.answer_index && <span className="text-[hsl(var(--primary))]">· correct</span>}</Label>
          <Input value={opt} onChange={(e)=>setOption(i, e.target.value)} data-testid={`cefr-q-option-${i}`} className="mt-2" />
        </div>
      ))}
    </div>
  );
}

function QuestionsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("_all");
  const [audienceFilter, setAudienceFilter] = useState("_all");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_Q);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/admin/cefr/questions"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function create(e) {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...draft, options: draft.options.filter(o => o.trim().length > 0) };
      if (payload.options.length < 2) { toast.error("At least 2 options required"); setBusy(false); return; }
      if (payload.answer_index >= payload.options.length) { toast.error("Correct option index is out of range"); setBusy(false); return; }
      await api.post("/admin/cefr/questions", payload);
      toast.success("Question added"); setDraft(EMPTY_Q); setCreating(false); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  async function saveEdit() {
    if (!editingId || !editDraft) return;
    setBusy(true);
    try {
      const payload = { ...editDraft, options: editDraft.options.filter(o => o.trim().length > 0) };
      if (payload.options.length < 2) { toast.error("At least 2 options required"); setBusy(false); return; }
      if (payload.answer_index >= payload.options.length) { toast.error("Correct option index is out of range"); setBusy(false); return; }
      await api.patch(`/admin/cefr/questions/${editingId}`, payload);
      toast.success("Saved"); setEditingId(null); setEditDraft(null); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this question?")) return;
    try { await api.delete(`/admin/cefr/questions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  const filtered = items.filter(q =>
    (filter === "_all" || q.level === filter) &&
    (audienceFilter === "_all" || (q.audience || "general") === audienceFilter)
  );
  const countsByLevel = LEVELS.reduce((acc, lvl) => ({ ...acc, [lvl]: items.filter(q => q.level === lvl && (audienceFilter === "_all" || (q.audience || "general") === audienceFilter)).length }), {});

  return (
    <div data-testid="cefr-questions-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] flex items-center gap-3 flex-wrap">
          <span>Question bank · {items.length}</span>
          {LEVELS.map(lvl => (
            <span key={lvl} className="text-[hsl(var(--primary))]">{lvl}: {countsByLevel[lvl]}</span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Select value={audienceFilter} onValueChange={setAudienceFilter}>
            <SelectTrigger data-testid="cefr-q-audience-filter" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All audiences</SelectItem>
              {AUDIENCES.map(a=><SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="cefr-q-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All levels</SelectItem>
              {LEVELS.map(lvl=><SelectItem key={lvl} value={lvl}>{lvl}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={()=>setCreating(v=>!v)} data-testid="cefr-q-new-toggle" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
            {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New question</>}
          </Button>
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 space-y-4" data-testid="cefr-q-create-form">
          <QuestionForm value={draft} onChange={setDraft} />
          <Button type="submit" disabled={busy} data-testid="cefr-q-create-submit" className="rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]">
            {busy ? "…" : "Add question"}
          </Button>
        </form>
      )}

      <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl max-h-[60vh] overflow-y-auto">
        {filtered.map((q) => editingId === q.id ? (
          <li key={q.id} data-testid={`cefr-q-row-${q.id}`} className="p-5 bg-[hsl(var(--muted))]/40">
            <QuestionForm value={editDraft} onChange={setEditDraft} />
            <div className="mt-3 flex items-center gap-2">
              <Button onClick={saveEdit} disabled={busy} data-testid={`cefr-q-save-${q.id}`} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"><Check className="w-4 h-4 mr-1.5" />Save</Button>
              <Button variant="outline" onClick={()=>{setEditingId(null); setEditDraft(null);}} data-testid={`cefr-q-cancel-${q.id}`} className="rounded-full"><X className="w-4 h-4 mr-1.5" />Cancel</Button>
            </div>
          </li>
        ) : (
          <li key={q.id} data-testid={`cefr-q-row-${q.id}`} className="p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] flex-wrap">
                <span className="text-[hsl(var(--primary))] font-medium">{q.level}</span>
                <span className="text-[hsl(var(--muted-foreground))]">· {q.kind}</span>
                <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${ (q.audience || 'general') === 'teen' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/60 border-[hsl(var(--border))]' }`} data-testid={`cefr-q-audience-badge-${q.id}`}>{q.audience || 'general'}</span>
              </div>
              {q.passage && <p className="text-xs text-[hsl(var(--muted-foreground))] italic mt-1 line-clamp-2">{q.passage}</p>}
              <p className="text-sm mt-1 line-clamp-2 font-medium">{q.prompt}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                <span className="text-[hsl(var(--secondary))]">✓ {q.options[q.answer_index]}</span>
              </p>
            </div>
            <div className="whitespace-nowrap">
              <Button size="sm" variant="ghost" onClick={()=>{setEditingId(q.id); setEditDraft({ ...q, audience: q.audience || 'general', passage: q.passage || "", options: [...q.options, ...Array(Math.max(0, 4-q.options.length)).fill("")] });}} data-testid={`cefr-q-edit-${q.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={()=>del(q.id)} data-testid={`cefr-q-delete-${q.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </li>
        ))}
        {filtered.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No questions at this filter.</li>}
      </ul>
    </div>
  );
}

function InvitesTab() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ audience: "general", for_student_name: "", for_email: "", notes: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/admin/cefr/invites"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function create(e) {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { audience: draft.audience };
      if (draft.for_email) payload.for_email = draft.for_email;
      if (draft.notes) payload.notes = draft.notes;
      if (draft.for_student_name) payload.for_student_name = draft.for_student_name;
      const { data } = await api.post("/admin/cefr/invites", payload);
      toast.success(`Code generated: ${data.code} (${data.audience})`);
      setDraft({ audience: draft.audience, for_student_name: "", for_email: "", notes: "" });
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this invite (and any in-progress attempt linked to it)?")) return;
    try { await api.delete(`/admin/cefr/invites/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  function copyCode(code) {
    navigator.clipboard?.writeText(code);
    toast.success(`Code ${code} copied`);
  }

  const statusTone = {
    unused: "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border-[hsl(var(--secondary))]/30",
    in_progress: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30",
    completed: "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/70 border-[hsl(var(--border))]",
    revoked: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
  };

  return (
    <div data-testid="cefr-invites-tab" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={create} className="lg:col-span-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4 h-fit" data-testid="cefr-invite-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Generate a test code</h3>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Test pool</Label>
          <Select value={draft.audience} onValueChange={(v)=>setDraft({...draft, audience: v})}>
            <SelectTrigger data-testid="cefr-invite-audience" className="mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUDIENCES.map(a=><SelectItem key={a.value} value={a.value} data-testid={`cefr-invite-audience-${a.value}`}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mt-1">{draft.audience === "teen" ? "Themes: school, friends, social media, hobbies." : "Themes: travel, work, mixed adult."}</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Student name (for your records)</Label>
          <Input value={draft.for_student_name} onChange={(e)=>setDraft({...draft, for_student_name: e.target.value})} data-testid="cefr-invite-name" className="mt-2" placeholder="e.g. Wei Chen" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Lock to email (optional)</Label>
          <Input type="email" value={draft.for_email} onChange={(e)=>setDraft({...draft, for_email: e.target.value})} data-testid="cefr-invite-email" className="mt-2" placeholder="Only this student account can use the code" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Notes</Label>
          <Textarea rows={2} value={draft.notes} onChange={(e)=>setDraft({...draft, notes: e.target.value})} data-testid="cefr-invite-notes" className="mt-2" />
        </div>
        <Button type="submit" disabled={busy} data-testid="cefr-invite-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{busy ? "…" : "Generate code"}</Button>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Codes are 8 characters, single-use. Share with the parent — student enters it on their portal to start the test.</p>
      </form>

      <div className="lg:col-span-7">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-3">Invites · {items.length}</h3>
        <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl max-h-[70vh] overflow-y-auto">
          {items.map((inv) => (
            <li key={inv.id} data-testid={`cefr-invite-row-${inv.id}`} className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-lg font-medium tracking-[0.2em] text-[hsl(var(--foreground))]" data-testid={`cefr-invite-code-${inv.id}`}>{inv.code}</code>
                  <button type="button" onClick={()=>copyCode(inv.code)} data-testid={`cefr-invite-copy-${inv.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><Copy className="w-3.5 h-3.5" /></button>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${ (inv.audience || 'general') === 'teen' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/60 border-[hsl(var(--border))]' }`} data-testid={`cefr-invite-audience-badge-${inv.id}`}>{inv.audience || 'general'}</span>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${statusTone[inv.status]}`} data-testid={`cefr-invite-status-${inv.id}`}>{inv.status.replace("_", " ")}</span>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {inv.for_student_name || "—"} {inv.for_email && <span>· {inv.for_email}</span>}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Created {format(parseISO(inv.created_at), "MMM dd HH:mm")}{inv.used_at && <> · used {format(parseISO(inv.used_at), "MMM dd HH:mm")}</>}</div>
                {inv.notes && <div className="text-xs italic text-[hsl(var(--muted-foreground))] mt-1">{inv.notes}</div>}
              </div>
              <Button variant="ghost" size="sm" onClick={()=>del(inv.id)} data-testid={`cefr-invite-delete-${inv.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
          {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No invites yet — generate one to authorise a student.</li>}
        </ul>
      </div>
    </div>
  );
}

function AttemptDetail({ attemptId }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/cefr/attempts/${attemptId}`); setDetail(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  })(); }, [attemptId]);

  if (busy) return <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]">Loading review…</div>;
  if (!detail) return null;
  const items = detail.answers_detailed || [];

  return (
    <div data-testid={`cefr-attempt-detail-${attemptId}`} className="mt-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4 space-y-3">
      <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Question-by-question review · {items.length} answered</div>
      <ol className="space-y-3">
        {items.map((it) => (
          <li key={`${attemptId}-${it.n}`} data-testid={`cefr-detail-row-${it.n}`} className="rounded-lg border border-[hsl(var(--border))] bg-white p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                Q{it.n} · <span className="text-[hsl(var(--primary))] font-medium">{it.level}</span>{it.kind ? <> · {it.kind}</> : null}
                {it.level_before !== it.level_after && <> · {it.level_before}→{it.level_after}</>}
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${it.correct ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {it.correct ? 'correct' : 'wrong'}
              </span>
            </div>
            {it.passage && <p className="text-xs italic text-[hsl(var(--muted-foreground))] mb-2 line-clamp-3">{it.passage}</p>}
            <p className="text-sm font-medium">{it.prompt}</p>
            <ul className="mt-2 space-y-1">
              {it.options.map((opt, i) => {
                const isStudent = i === it.student_answer_index;
                const isCorrect = i === it.correct_answer_index;
                const tone = isCorrect
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  : isStudent
                    ? 'border-red-300 bg-red-50 text-red-900'
                    : 'border-[hsl(var(--border))] bg-white text-[hsl(var(--foreground))]/70';
                return (
                  <li key={i} data-testid={`cefr-detail-${attemptId}-q${it.n}-opt${i}`} className={`flex items-start gap-2 text-xs px-2.5 py-1.5 rounded-md border ${tone}`}>
                    <span className="font-mono">{String.fromCharCode(65 + i)}</span>
                    <span className="flex-1">{opt}</span>
                    {isCorrect && <span className="text-[10px] uppercase tracking-[0.15em] font-medium">correct ✓</span>}
                    {isStudent && !isCorrect && <span className="text-[10px] uppercase tracking-[0.15em] font-medium">student ✗</span>}
                    {isStudent && isCorrect && <span className="text-[10px] uppercase tracking-[0.15em] font-medium">student ✓</span>}
                  </li>
                );
              })}
              {it.options.length === 0 && <li className="text-xs italic text-[hsl(var(--destructive))]">Question text not found — it may have been deleted from the bank after the test.</li>}
            </ul>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-[hsl(var(--muted-foreground))] italic">No answers recorded.</li>}
      </ol>
    </div>
  );
}

function AttemptsTab() {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  async function load() {
    try { const { data } = await api.get("/admin/cefr/attempts"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  const levelTone = { A1: "bg-red-50 text-red-700 border-red-200", A2: "bg-orange-50 text-orange-700 border-orange-200", B1: "bg-amber-50 text-amber-700 border-amber-200", B2: "bg-lime-50 text-lime-700 border-lime-200", C1: "bg-emerald-50 text-emerald-700 border-emerald-200", C2: "bg-teal-50 text-teal-800 border-teal-200" };

  async function exportCSV() {
    if (items.length === 0) { toast.error("No attempts to export"); return; }
    // We need answer-level detail for the CSV. Pull each attempt detail in parallel.
    const rows = [];
    try {
      const details = await Promise.all(items.map((a) => api.get(`/admin/cefr/attempts/${a.id}`).then((r) => r.data).catch(() => null)));
      for (const det of details) {
        if (!det) continue;
        if (!det.answers_detailed || det.answers_detailed.length === 0) {
          rows.push({
            student: det.student_name || det.student_email, email: det.student_email, code: det.invite_code,
            audience: det.audience || "general",
            status: det.status, final_level: det.final_level || "", q_n: "", q_level: "", q_kind: "",
            q_prompt: "", student_answer: "", correct_answer: "", correct: "",
            started_at: det.started_at, finished_at: det.finished_at || "",
          });
          continue;
        }
        for (const a of det.answers_detailed) {
          const sAns = a.options[a.student_answer_index];
          const cAns = a.options[a.correct_answer_index];
          rows.push({
            student: det.student_name || det.student_email, email: det.student_email, code: det.invite_code,
            audience: det.audience || "general",
            status: det.status, final_level: det.final_level || "",
            q_n: a.n, q_level: a.level, q_kind: a.kind || "",
            q_prompt: a.prompt,
            student_answer: `${String.fromCharCode(65 + a.student_answer_index)}. ${sAns ?? ""}`,
            correct_answer: `${String.fromCharCode(65 + a.correct_answer_index)}. ${cAns ?? ""}`,
            correct: a.correct ? "yes" : "no",
            started_at: det.started_at, finished_at: det.finished_at || "",
          });
        }
      }
      const stamp = format(new Date(), "yyyyMMdd-HHmm");
      downloadCSV(`dawnville-cefr-attempts-${stamp}.csv`, rows, [
        { key: "student", label: "Student" },
        { key: "email", label: "Email" },
        { key: "code", label: "Test code" },
        { key: "audience", label: "Audience" },
        { key: "status", label: "Status" },
        { key: "final_level", label: "Final level" },
        { key: "started_at", label: "Started" },
        { key: "finished_at", label: "Finished" },
        { key: "q_n", label: "Q#" },
        { key: "q_level", label: "Q level" },
        { key: "q_kind", label: "Q kind" },
        { key: "q_prompt", label: "Question" },
        { key: "student_answer", label: "Student answer" },
        { key: "correct_answer", label: "Correct answer" },
        { key: "correct", label: "Correct?" },
      ]);
      toast.success(`Exported ${rows.length} row(s) across ${items.length} attempt(s)`);
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="cefr-attempts-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Attempts · {items.length}</h3>
        <Button size="sm" variant="outline" onClick={exportCSV} disabled={items.length === 0} data-testid="cefr-attempts-export-csv" className="rounded-full"><Copy className="w-3.5 h-3.5 mr-1.5" />Export CSV (with answers)</Button>
      </div>
      <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
        {items.map((a) => {
          const expanded = expandedId === a.id;
          return (
            <li key={a.id} data-testid={`cefr-attempt-row-${a.id}`} className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium" data-testid={`cefr-attempt-student-${a.id}`}>{a.student_name || a.student_email}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2 flex-wrap">
                    <span>Code <code className="font-mono">{a.invite_code}</code></span>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full border ${ (a.audience || 'general') === 'teen' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/60 border-[hsl(var(--border))]' }`} data-testid={`cefr-attempt-audience-${a.id}`}>{a.audience || 'general'}</span>
                    <span>· {a.status === "completed" ? `finished ${format(parseISO(a.finished_at), "MMM dd HH:mm")}` : `started ${format(parseISO(a.started_at), "MMM dd HH:mm")}`}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.final_level ? (
                    <div className={`px-3 py-1.5 rounded-full border text-sm font-medium ${levelTone[a.final_level] || ""}`} data-testid={`cefr-attempt-level-${a.id}`}>
                      Final · {a.final_level}
                    </div>
                  ) : (
                    <div className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30">in progress · {a.answers?.length || 0}/20</div>
                  )}
                  <Button size="sm" variant="outline" onClick={()=>setExpandedId(expanded ? null : a.id)} data-testid={`cefr-attempt-toggle-${a.id}`} className="rounded-full text-xs">
                    {expanded ? <><X className="w-3.5 h-3.5 mr-1.5" />Close review</> : <><Pencil className="w-3.5 h-3.5 mr-1.5" />Review answers</>}
                  </Button>
                </div>
              </div>
              {a.level_stats && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {LEVELS.map(lvl => {
                    const s = a.level_stats[lvl];
                    if (!s || s.total === 0) return null;
                    return (
                      <span key={lvl} className="px-2 py-1 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/70">
                        {lvl}: {s.correct}/{s.total} ({Math.round(s.ratio * 100)}%)
                      </span>
                    );
                  })}
                </div>
              )}
              {expanded && <AttemptDetail attemptId={a.id} />}
            </li>
          );
        })}
        {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No attempts yet.</li>}
      </ul>
    </div>
  );
}

export default function CefrPanel() {
  const [tab, setTab] = useState("invites");
  return (
    <div data-testid="admin-cefr-panel" className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="cefr-subtabs" className="bg-[hsl(var(--muted))] p-1 rounded-full h-auto">
          <TabsTrigger value="invites" data-testid="cefr-subtab-invites" className="rounded-full data-[state=active]:bg-white px-5 py-2">Invites</TabsTrigger>
          <TabsTrigger value="questions" data-testid="cefr-subtab-questions" className="rounded-full data-[state=active]:bg-white px-5 py-2">Question bank</TabsTrigger>
          <TabsTrigger value="attempts" data-testid="cefr-subtab-attempts" className="rounded-full data-[state=active]:bg-white px-5 py-2">Attempts</TabsTrigger>
        </TabsList>
        <TabsContent value="invites" className="mt-6"><InvitesTab /></TabsContent>
        <TabsContent value="questions" className="mt-6"><QuestionsTab /></TabsContent>
        <TabsContent value="attempts" className="mt-6"><AttemptsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
